import { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, Coffee, X, AlertTriangle, Play, ChevronRight, MessageSquare, Save, ClipboardCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const PlanningView = ({ user }) => {
  const [weekSchedules, setWeekSchedules] = useState([]);
  const [nextWeekSchedules, setNextWeekSchedules] = useState([]);
  const [nextWeekPublication, setNextWeekPublication] = useState({ status: 'draft', published_at: null });
  const [photoStatus, setPhotoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState({});
  const [savingComment, setSavingComment] = useState(null);
  const [activeWeek, setActiveWeek] = useState('current'); // 'current' or 'next'

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Calculate Monday of current week (week MUST start on Monday)
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Calculate days to subtract to get to Monday
      let daysToMonday;
      if (dayOfWeek === 0) {
        daysToMonday = 6; // Sunday -> go back 6 days to previous Monday
      } else {
        daysToMonday = dayOfWeek - 1; // Other days -> go back (dayOfWeek - 1) days
      }
      
      const monday = new Date(today);
      monday.setDate(today.getDate() - daysToMonday);
      monday.setHours(0, 0, 0, 0);
      
      // End of next week (14 days total: current week + next week)
      const nextWeekSunday = new Date(monday);
      nextWeekSunday.setDate(monday.getDate() + 13); // 14 days - 1
      nextWeekSunday.setHours(23, 59, 59, 999);

      // Use local date formatting to avoid timezone issues
      const startDate = formatDateLocal(monday);
      const endDate = formatDateLocal(nextWeekSunday);
      const nextMonday = new Date(monday);
      nextMonday.setDate(monday.getDate() + 7);
      const nextWeekStart = formatDateLocal(nextMonday);

      console.log('📅 Two weeks calculation:', { today: formatDateLocal(today), startDate, endDate });

      const [schedRes, publicationRes] = await Promise.all([
        axios.get(
          `${API}/schedule/user/${user.user_id}?start_date=${startDate}&end_date=${endDate}`,
          { withCredentials: true }
        ),
        axios.get(
          `${API}/schedule/week-publication-status?week_start=${nextWeekStart}`,
          { withCredentials: true }
        ).catch(() => ({ data: { status: 'draft', published_at: null } }))
      ]);
      const isNextWeekPublished = publicationRes.data?.status === 'published';
      setNextWeekPublication(publicationRes.data || { status: 'draft', published_at: null });
      
      // Build current week array (7 days: Mon to Sun)
      const currentWeek = [];
      const nextWeek = [];
      const commentsInit = {};
      const currentDate = new Date(monday);
      
      // Current week (days 0-6)
      for (let i = 0; i < 7; i++) {
        const dateStr = formatDateLocal(currentDate);
        const existingSchedule = schedRes.data.find(s => s.date === dateStr);
        currentWeek.push({
          date: dateStr,
          status: existingSchedule?.status || 'repos',  // Default: repos (no schedule = rest day)
          loading_time: existingSchedule?.loading_time || null,
          start_time: existingSchedule?.start_time || null,
          second_start_time: existingSchedule?.second_start_time || null,
          end_time: existingSchedule?.end_time || null,
          tour_type: existingSchedule?.tour_type || null,
          site_name: existingSchedule?.site_name || null,
          comment: existingSchedule?.comment || null,
          admin_comment: existingSchedule?.admin_comment || null,
          employee_comment: existingSchedule?.employee_comment || null,
          missions_count: existingSchedule?.missions_count || 0,
          formation_title: existingSchedule?.formation_title || null
        });
        commentsInit[dateStr] = existingSchedule?.employee_comment || '';
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Next week (days 7-13)
      for (let i = 0; i < 7; i++) {
        const dateStr = formatDateLocal(currentDate);
        const existingSchedule = schedRes.data.find(s => s.date === dateStr);
        nextWeek.push({
          date: dateStr,
          status: existingSchedule?.status || 'repos',  // Default: repos (no schedule = rest day)
          loading_time: existingSchedule?.loading_time || null,
          start_time: existingSchedule?.start_time || null,
          second_start_time: existingSchedule?.second_start_time || null,
          end_time: existingSchedule?.end_time || null,
          tour_type: existingSchedule?.tour_type || null,
          site_name: existingSchedule?.site_name || null,
          comment: existingSchedule?.comment || null,
          admin_comment: existingSchedule?.admin_comment || null,
          employee_comment: existingSchedule?.employee_comment || null,
          missions_count: existingSchedule?.missions_count || 0,
          formation_title: existingSchedule?.formation_title || null
        });
        commentsInit[dateStr] = existingSchedule?.employee_comment || '';
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      console.log('📅 Current week:', currentWeek.map(w => w.date));
      console.log('📅 Next week:', nextWeek.map(w => w.date));
      
      setWeekSchedules(currentWeek);
      setNextWeekSchedules(isNextWeekPublished ? nextWeek : []);
      setComments(commentsInit);
      if (!isNextWeekPublished) {
        setActiveWeek('current');
      }

      const photoRes = await axios.get(`${API}/photos/session-status`, { withCredentials: true });
      setPhotoStatus(photoRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveComment = async (date) => {
    setSavingComment(date);
    try {
      // Check if this is today's date
      const today = new Date().toISOString().split('T')[0];
      
      if (date === today) {
        // Use the dedicated employee comment endpoint for today
        await axios.put(`${API}/schedule/today/comment`, {
          comment: comments[date]
        }, { withCredentials: true });
      } else {
        // For other dates, use the schedule endpoint with employee_comment
        await axios.post(`${API}/schedule/employee-comment`, {
          date: date,
          comment: comments[date]
        }, { withCredentials: true });
      }
      
      setWeekSchedules(prev => prev.map(s => 
        s.date === date ? { ...s, employee_comment: comments[date] } : s
      ));
      
      alert('Commentaire enregistré !');
    } catch (error) {
      console.error('Failed to save comment:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du commentaire');
    } finally {
      setSavingComment(null);
    }
  };

  const getStatusInfo = (schedule) => {
    const status = schedule?.status;
    const normalized = (status || '').toLowerCase();
    switch (status) {
      case 'prevu': return { icon: <ClipboardCheck className="w-5 h-5" />, label: 'Planifié', color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-300' };
      case 'present': return { icon: <CheckCircle className="w-5 h-5" />, label: 'Présent ✓', color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-400' };
      case 'repos': return { icon: <Coffee className="w-5 h-5" />, label: 'Repos', color: 'text-gray-600', bg: 'bg-gray-200', border: 'border-gray-400' };
      case 'repos_force': return { icon: <Coffee className="w-5 h-5" />, label: 'Repos forcé', color: 'text-fuchsia-800', bg: 'bg-fuchsia-100', border: 'border-fuchsia-400' };
      case 'disponible': return { icon: <Coffee className="w-5 h-5" />, label: 'Disponible', color: 'text-cyan-700', bg: 'bg-cyan-100', border: 'border-cyan-300' };
      case 'repos_demande': return { icon: <Coffee className="w-5 h-5" />, label: 'Repos (validé)', color: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-400' };
      case 'absent': return { icon: <X className="w-5 h-5" />, label: 'Absent', color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-300' };
      case 'conge': return { icon: <Calendar className="w-5 h-5" />, label: 'Congé', color: 'text-purple-600', bg: 'bg-purple-100', border: 'border-purple-300' };
      case 'cp': return { icon: <X className="w-5 h-5" />, label: 'CP', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'css': return { icon: <X className="w-5 h-5" />, label: 'CSS', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'am': return { icon: <X className="w-5 h-5" />, label: 'AM', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'map': return { icon: <X className="w-5 h-5" />, label: 'MAP', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'fdc': return { icon: <X className="w-5 h-5" />, label: 'FDC', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'fpe': return { icon: <X className="w-5 h-5" />, label: 'FPE', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'dem': return { icon: <X className="w-5 h-5" />, label: 'Démission', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'adp': return { icon: <X className="w-5 h-5" />, label: 'ADP', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'formation_binome': return { icon: <ClipboardCheck className="w-5 h-5" />, label: 'Formation Binôme', color: 'text-indigo-600', bg: 'bg-indigo-100', border: 'border-indigo-300' };
      case 'formation_amzl': return { icon: <ClipboardCheck className="w-5 h-5" />, label: 'Formation AMZL', color: 'text-teal-600', bg: 'bg-teal-100', border: 'border-teal-300' };
      case 'formation_safety': return { icon: <ClipboardCheck className="w-5 h-5" />, label: 'Formation Safety', color: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-emerald-300' };
      case 'annule': return { icon: <X className="w-5 h-5" />, label: 'Annulé', color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300' };
      case 'en_cours_integration': return { icon: <ClipboardCheck className="w-5 h-5" />, label: 'En cours d’intégration', color: 'text-slate-700', bg: 'bg-slate-200', border: 'border-slate-300' };
      default: return { icon: <ClipboardCheck className="w-5 h-5" />, label: normalized ? normalized.replaceAll('_', ' ') : 'Non défini', color: 'text-slate-700', bg: 'bg-slate-100', border: 'border-slate-300' };
    }
  };

  const isAmazonSchedule = (schedule) => {
    const siteName = (schedule?.site_name || '').toLowerCase();
    if (siteName) {
      return !(siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive'));
    }
    const amazonTourTypes = new Set([
      'NEXT', 'SDA', 'SDB', 'SDC', 'SDA+C', 'SDB+C', 'SDA+B', 'SDC+B',
      "Assistance chef d'équipe", "Assistance chef d'equipe", 'Backup'
    ]);
    return amazonTourTypes.has(schedule?.tour_type);
  };

  const getAmazonTimeDisplay = (schedule) => {
    if (!schedule?.tour_type) return null;
    const first = schedule.start_time || null;
    const second = schedule.second_start_time || null;
    if (first && second) {
      return `${first} • ${second}`;
    }
    return first || second || null;
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const isNextWeekPublished = nextWeekPublication?.status === 'published';
  const photosIncomplete = photoStatus && (!photoStatus.start?.complete || !photoStatus.end?.complete);
  const missionsValidated = weekSchedules.filter(s => s.status === 'present' && s.validation_status === 'approved').length;

  return (
    <Layout user={user}>
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-['Chivo']">Mon Planning</h1>
            <p className="text-sm text-gray-500">Semaine complète</p>
          </div>
          <div className="text-right">
            <p className="text-2xl sm:text-3xl font-bold text-green-600">{missionsValidated}</p>
            <p className="text-xs sm:text-sm text-gray-500">mission(s) validée(s)</p>
          </div>
        </div>

        {/* Légende des couleurs */}
        <div className="flex flex-wrap gap-2 sm:gap-3 p-3 bg-gray-100 rounded-lg">
          <span className="text-xs sm:text-sm font-medium text-gray-600 w-full sm:w-auto">Légende :</span>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-gray-400"></span>
            <span className="text-xs sm:text-sm">Repos</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-amber-500"></span>
            <span className="text-xs sm:text-sm">Repos (validé)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-blue-500"></span>
            <span className="text-xs sm:text-sm">Prévu</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-green-500"></span>
            <span className="text-xs sm:text-sm">Présent</span>
          </div>
        </div>

        {/* Mission Status & Quick Action */}
        <Card className={`border-2 ${photoStatus?.start?.complete && photoStatus?.end?.complete ? 'border-green-300 bg-green-50' : photoStatus?.start?.complete ? 'border-blue-300 bg-blue-50' : 'border-orange-300 bg-orange-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${
                  photoStatus?.start?.complete && photoStatus?.end?.complete 
                    ? 'bg-green-500' 
                    : photoStatus?.start?.complete 
                      ? 'bg-blue-500' 
                      : 'bg-orange-500'
                }`}>
                  {photoStatus?.start?.complete && photoStatus?.end?.complete ? (
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  ) : (
                    <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className={`font-semibold text-sm sm:text-base ${
                    photoStatus?.start?.complete && photoStatus?.end?.complete 
                      ? 'text-green-700' 
                      : photoStatus?.start?.complete 
                        ? 'text-blue-700' 
                        : 'text-orange-700'
                  }`}>
                    {photoStatus?.start?.complete && photoStatus?.end?.complete 
                      ? 'Mission terminée' 
                      : photoStatus?.start?.complete 
                        ? 'Mission en cours' 
                        : 'Prêt à commencer'}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Début: {photoStatus?.start?.complete ? '✅' : '⏳'} | Fin: {photoStatus?.end?.complete ? '✅' : '⏳'}
                  </p>
                </div>
              </div>
              <Link to="/photos">
                <Button className={`${
                  photoStatus?.start?.complete && photoStatus?.end?.complete 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-orange-600 hover:bg-orange-700'
                } text-white min-h-[44px] px-4 rounded-xl text-sm shrink-0`}>
                  {photoStatus?.start?.complete && photoStatus?.end?.complete 
                    ? 'Détails' 
                    : photoStatus?.start?.complete 
                      ? 'Fin' 
                      : 'Début'
                  }
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Mon Planning
                </CardTitle>
              </div>
              {/* Week Tabs */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setActiveWeek('current')}
                  data-testid="employee-planning-current-week-tab"
                  className={`flex-1 px-3 sm:px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                    activeWeek === 'current'
                      ? 'bg-[#0F172A] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
                  }`}
                >
                  Semaine actuelle
                </button>
                {isNextWeekPublished && (
                  <button
                    onClick={() => setActiveWeek('next')}
                    data-testid="employee-planning-next-week-tab"
                    className={`flex-1 px-3 sm:px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      activeWeek === 'next'
                        ? 'bg-[#0F172A] text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
                    }`}
                  >
                    Semaine prochaine
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(activeWeek === 'next' && isNextWeekPublished ? nextWeekSchedules : weekSchedules).map((schedule) => {
                  const statusInfo = getStatusInfo(schedule);
                  const isToday = schedule.date === todayStr;
                  const isPast = schedule.date < todayStr;
                  const showAmazonInfo = isAmazonSchedule(schedule) && (schedule.status === 'prevu' || schedule.status === 'present');
                  const amazonTimeDisplay = showAmazonInfo ? getAmazonTimeDisplay(schedule) : null;
                  
                  return (
                    <div
                      key={schedule.date}
                      className={`p-3 sm:p-4 rounded-xl transition-all border-2 ${
                        isToday ? 'bg-[#0F172A] text-white shadow-lg ring-2 sm:ring-4 ring-blue-400 border-[#0F172A]' : 
                        schedule.status === 'present' ? 'bg-green-100 border-green-400' :
                        schedule.status === 'repos' ? 'bg-gray-200 border-gray-400' :
                        schedule.status === 'repos_force' ? 'bg-fuchsia-100 border-fuchsia-400' :
                        schedule.status === 'en_cours_integration' ? 'bg-slate-100 border-slate-300' :
                        schedule.status === 'repos_demande' ? 'bg-amber-100 border-amber-400' :
                        schedule.status === 'prevu' ? 'bg-blue-50 border-blue-300' :
                        isPast ? 'bg-gray-100 border-gray-300 opacity-70' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2 sm:mb-3 gap-2">
                        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${
                            isToday ? 'bg-white/20' : statusInfo.bg
                          }`}>
                            <span className={isToday ? 'text-white' : statusInfo.color}>
                              {statusInfo.icon}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className={`font-semibold text-sm sm:text-base ${isToday ? 'text-white' : 'text-[#0F172A]'}`}>
                              {new Date(schedule.date).toLocaleDateString('fr-FR', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short'
                              })}
                              {isToday && (
                                <span className="ml-1.5 text-[10px] sm:text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                                  Aujourd&apos;hui
                                </span>
                              )}
                            </p>
                            {schedule.site_name && (
                              <p className={`text-xs sm:text-sm ${isToday ? 'text-gray-200' : 'text-gray-600'}`} data-testid={`employee-planning-site-${schedule.date}`}>
                                Site: {schedule.site_name}
                              </p>
                            )}
                            {showAmazonInfo && schedule.tour_type && (
                              <p className={`text-xs sm:text-sm ${isToday ? 'text-gray-200' : 'text-blue-700'}`} data-testid={`employee-planning-prestation-${schedule.date}`}>
                                Prestation: {schedule.tour_type}
                              </p>
                            )}
                            {showAmazonInfo && amazonTimeDisplay && (
                              <p className={`text-xs sm:text-sm flex items-center gap-1 ${isToday ? 'text-gray-300' : 'text-gray-500'}`} data-testid={`employee-planning-amazon-time-${schedule.date}`}>
                                <Clock className="w-3 h-3" /> {amazonTimeDisplay}
                              </p>
                            )}
                            {!showAmazonInfo && schedule.loading_time && (schedule.status === 'prevu' || schedule.status === 'present') && (
                              <p className={`text-xs sm:text-sm flex items-center gap-1 ${isToday ? 'text-gray-300' : 'text-gray-500'}`}>
                                <Clock className="w-3 h-3" /> {schedule.loading_time}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold shrink-0 ${
                          isToday ? 'bg-white/20 text-white' : 
                          schedule.status === 'repos' ? 'bg-gray-500 text-white' :
                          schedule.status === 'repos_force' ? 'bg-fuchsia-700 text-white' :
                        schedule.status === 'en_cours_integration' ? 'bg-slate-500 text-white' :
                          schedule.status === 'repos_demande' ? 'bg-amber-500 text-white' :
                          schedule.status === 'present' ? 'bg-green-500 text-white' :
                          schedule.status === 'prevu' ? 'bg-blue-500 text-white' :
                          `${statusInfo.bg} ${statusInfo.color}`
                        }`}>
                          {statusInfo.label}
                        </div>
                      </div>

                      {/* Comment input - only show if no comment sent yet */}
                      <div className="space-y-2">
                        {/* Admin Comment - Important info from admin */}
                        {schedule.admin_comment && (
                          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${
                            isToday ? 'bg-orange-400/30 border border-orange-300/50' : 'bg-orange-50 border border-orange-200'
                          }`}>
                            <span className="text-lg">📌</span>
                            <div className="flex-1">
                              <p className={`text-xs font-semibold mb-0.5 ${isToday ? 'text-orange-200' : 'text-orange-700'}`}>
                                Message de l&apos;administration
                              </p>
                              <p className={`text-sm ${isToday ? 'text-white' : 'text-orange-800'}`}>
                                {schedule.admin_comment}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Display Rdv Next comment only if working (not repos/absent) */}
                        {schedule.comment && 
                          (schedule.comment.includes('Rdv') || schedule.comment === 'Rdv Next') &&
                          !schedule.comment.includes('Tournée:') && 
                          !schedule.comment.includes('Livrés:') &&
                          !['repos', 'repos_force', 'repos_demande', 'absent', 'cp', 'css', 'am', 'fdc', 'fpe', 'dem', 'adp', 'map', 'en_cours_integration'].includes(schedule.status) && (
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                            isToday ? 'bg-white/10' : 'bg-blue-50 border border-blue-200'
                          }`}>
                            <ClipboardCheck className={`w-4 h-4 ${isToday ? 'text-white' : 'text-blue-600'}`} />
                            <span className={`font-semibold text-sm ${isToday ? 'text-white' : 'text-blue-800'}`}>
                              📍 {schedule.comment}
                            </span>
                          </div>
                        )}
                        
                        {/* Show existing comment (read-only) or input field */}
                        {schedule.employee_comment ? (
                          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${
                            isToday ? 'bg-white/10' : 'bg-orange-50 border border-orange-200'
                          }`}>
                            <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isToday ? 'text-white' : 'text-orange-600'}`} />
                            <div className="flex-1">
                              <p className={`text-sm ${isToday ? 'text-white' : 'text-orange-800'}`}>
                                {schedule.employee_comment}
                              </p>
                              <p className={`text-xs mt-1 ${isToday ? 'text-gray-300' : 'text-orange-500'}`}>
                                ✓ Commentaire envoyé (non modifiable)
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <MessageSquare className={`w-4 h-4 flex-shrink-0 ${isToday ? 'text-gray-300' : 'text-gray-400'}`} />
                            <Input
                              value={comments[schedule.date] || ''}
                              onChange={(e) => setComments(prev => ({ ...prev, [schedule.date]: e.target.value }))}
                              placeholder="Commentaire..."
                              className={`flex-1 h-10 sm:h-9 text-sm ${
                                isToday ? 'bg-white/10 border-white/20 text-white placeholder:text-gray-400' : ''
                              }`}
                            />
                            {comments[schedule.date]?.trim() && (
                              <Button
                                size="sm"
                                onClick={() => saveComment(schedule.date)}
                                disabled={savingComment === schedule.date}
                                className={`min-h-[40px] min-w-[40px] ${isToday ? 'bg-white/20 hover:bg-white/30' : 'btn-primary'}`}
                              >
                                {savingComment === schedule.date ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                  <Save className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default PlanningView;
